// Universo por fase, cotas e seleção de cascatas do fluxo Normal — ports 1:1 do
// monólito (index.tsx:3451-3939 e 2288-2448). PARIDADE ESTRITA: bugs conhecidos são
// preservados e anotados (COT-06). Nenhum import de React (AD-002).

import { NormalRuleContext, userCodEtapa } from './context';
import { canReset, getPlayerStep, getTaskStepLocal, hasTaskFeedbackForUser, taskAppreciationExpired } from './rules';

// Cópia local de utils/helpers.clamp (o módulo global carrega JSX; regras não
// importam React).
const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

// Último avaliador corrente da task é o gestor (ou alguém da sua dupla) — port de
// index.tsx:3113-3140. O retorno original podia ser `null` (dupla ausente); aqui é
// coagido com !! — todos os consumidores usam apenas a veracidade.
// `evaluatorPlayer` (opcional) troca o player de referência da comparação — usado
// pelos ramos de lateralidade da COT-06 para reconhecer a task pendente com a
// PRÓPRIA substituta (apontada como próxima avaliadora na validação).
export function isCurrentEvaluator(ctx: NormalRuleContext, task: any, evaluatorPlayer?: any): boolean {
  const { internalUser } = ctx;
  const playerInfo: any = evaluatorPlayer ?? ctx.playerInfo;

  if (!internalUser || !task || !ctx.playerInfo || !playerInfo) return false;

  let evaluators: any[] = [];

  if (!!task.original_manager?.length) {
    evaluators.push(task.original_manager);
  }

  if (Array.isArray(task.evaluator) && task.evaluator?.length) {
    evaluators = evaluators.concat(task.evaluator);
  }

  // Elo null/undefined em `evaluator` existe em dados reais (push de id não resolvido
  // vira null no JSON) — só strings participam; o último elo VÁLIDO responde.
  evaluators = evaluators.filter(x => typeof x === 'string' && !x.match(/selo/gi));

  if (evaluators.length) {
    const matriculaEvaluator = parseInt(evaluators[evaluators.length - 1].replace('F', ''));
    const currentId = parseInt(playerInfo._id);
    const currentIdMad = Array.isArray(playerInfo.mad_dupla) ? playerInfo.mad_dupla.map((md: any) => parseInt(md._id)) : null;

    return matriculaEvaluator === currentId || !!(currentIdMad && currentIdMad.some((id: any) => matriculaEvaluator === id));
  } else {
    return false;
  }
}

// Universo da fase selecionada (COT-01) — port de index.tsx:3458-3494, SEM os
// filtros de tab (COT-02/03): no fluxo separado a divisão por tabs vira divisão
// por fluxo/aggregate.
export function filterStageData(ctx: NormalRuleContext, data: any[] | undefined): any[] {
  const playerInfo: any = ctx.playerInfo;
  const activeStageObj: any = ctx.activeStageObj;

  let stageData: any = data?.filter(item => {
    return !!activeStageObj && item.created >= activeStageObj?.date_start && item.created <= activeStageObj?.date_finish;
  });

  if (activeStageObj?.type && activeStageObj?.type === 'semester') {
    stageData = stageData?.filter((item: any) => {
      const taskStep = getTaskStepLocal(item);
      return (
        (taskStep !== undefined && taskStep >= 5) ||
        getPlayerStep(ctx, item) >= 5 ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(item.original_manager)) &&
          userCodEtapa(ctx, null, !!item.mad) &&
          userCodEtapa(ctx, null, !!item.mad) === 5) ||
        (String(item.original_manager) === String(playerInfo._id) &&
          userCodEtapa(ctx, null, !!item.mad) &&
          userCodEtapa(ctx, null, !!item.mad) === 5)
      );
    });
  } else if (activeStageObj?.type && activeStageObj?.type === 'trimester') {
    stageData = stageData?.filter((item: any) => {
      const taskStep = getTaskStepLocal(item);
      return (
        (taskStep !== undefined && taskStep >= 4 && taskStep < 5) ||
        (getPlayerStep(ctx, item) >= 4 && getPlayerStep(ctx, item) < 5) ||
        (Array.isArray(playerInfo?.mad_dupla) &&
          playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(item.original_manager)) &&
          userCodEtapa(ctx, null, !!item.mad) &&
          userCodEtapa(ctx, null, !!item.mad) === 4) ||
        (String(item.original_manager) === String(playerInfo._id) &&
          userCodEtapa(ctx, null, !!item.mad) &&
          userCodEtapa(ctx, null, !!item.mad) === 4)
      );
    });
  }

  if (!stageData) stageData = [];

  return stageData;
}

export interface NormalQuotas {
  maxValidTasks: number;
  maxRejectedTasks: number;
  feedbackTasks: number;
  dataWithoutProsseguir: any[];
  maxDoubledTasks: number;
  maxPromotedTasks: number;
  validTasks: any[];
  rejectedTasks: any[];
  doubledTasks: any[];
  promotedTasks: any[];
  maxValeuTasks: number;
  valeuTasks: any[];
  maxMandouBemTasks: number;
  mandouBemTasks: any[];
  maxUauTasks: number;
  uauTasks: any[];
}

// Cotas e universos por fase (COT-04..09) — port de index.tsx:3574-3910.
export function computeQuotas(ctx: NormalRuleContext, stageData: any[]): NormalQuotas {
  const { isVacationManager, getActiveEvaluationStep } = ctx;
  const playerInfo: any = ctx.playerInfo;
  const userInfo: any = ctx.userInfo;
  const activeStageObj: any = ctx.activeStageObj;

  // No monólito: userCodEtapa(null, activeTabDirector === 'mad') — no fluxo Normal
  // (tabs diretoria/rede) o verifyMad é sempre false.
  const isDirector = userCodEtapa(ctx, null, false) && userCodEtapa(ctx, null, false) === 5;

  // COT-04 — para não-cod5, tasks paradas comigo como assessor saem do universo de cotas
  let dataWithoutProsseguir = stageData;

  if (!isDirector) {
    dataWithoutProsseguir = stageData?.filter((task: any) => {
      if (!!task.prosseguir_idGestor && task.prosseguir_idGestor === playerInfo._id) return false;

      return true;
    });
  }

  // COT-05
  const maxValidTasks = stageData.length;
  const maxRejectedTasks = stageData.length;
  const feedbackTasks = stageData.filter((task: any) => {
    return hasTaskFeedbackForUser(ctx, task);
  }).length;

  // COT-06 — base da cota de bonificação
  const currentMaxDoubledTasks = dataWithoutProsseguir.filter((task: any) => {
    const _taskStep = getTaskStepLocal(task);
    // COT-06: hora do cliente (bug preservado por paridade) — o original usa
    // `+new Date()` em vez de realDatetime nos ramos de lateralidade.
    const date = ctx.clientNow();

    if (Array.isArray(task.prosseguir_managers) && !!task.prosseguir_managers.includes(playerInfo._id)) return false;

    // Gestor imediato apreciando realização expirada. 2026-07-23: além da task
    // pendente com o substituído, conta a pendente com a PRÓPRIA substituta — na
    // validação do gerente de equipe o próximo avaliador do Nível 1 pode ser a
    // gestora imediata (evaluator[último] = ela); sem isso a base fica 0 e o D-01
    // barra a dobra que V-02/R-04/D-03 autorizam.
    if (
      taskAppreciationExpired(ctx, task) &&
      isVacationManager &&
      (userInfo?._id === playerInfo?.gestor ||
        (Array.isArray(userInfo?.mad_dupla) &&
          userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
      activeStageObj &&
      (isCurrentEvaluator(ctx, task) || isCurrentEvaluator(ctx, task, userInfo))
    ) {
      if (
        !getActiveEvaluationStep &&
        ((userCodEtapa(ctx, userInfo, !!task.mad) === 4 && date < activeStageObj.date_evaluation_step4_start) ||
          (userCodEtapa(ctx, userInfo, !!task.mad) === 5 && date < activeStageObj.date_evaluation_step5_start))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        ((getActiveEvaluationStep > 3 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 4) ||
          (getActiveEvaluationStep > 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 5))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        userCodEtapa(ctx, null, !!task.mad) === 4 &&
        date > activeStageObj.date_evaluation_step4_start &&
        (getTaskStepLocal(task) as number) < 4
      ) {
        return true;
      }

      return false;
    }

    // Gestor imediato apreciando realização expirada (bloco redundante preservado —
    // o original tem `return false` comentado com TO DO; sem match, o fluxo SEGUE)
    if (
      isVacationManager &&
      (userInfo?._id === playerInfo?.gestor ||
        (Array.isArray(userInfo?.mad_dupla) &&
          userInfo?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))) &&
      activeStageObj &&
      (isCurrentEvaluator(ctx, task) || isCurrentEvaluator(ctx, task, userInfo))
    ) {
      if (
        !getActiveEvaluationStep &&
        ((userCodEtapa(ctx, userInfo, !!task.mad) === 4 && date < activeStageObj.date_evaluation_step4_start) ||
          (userCodEtapa(ctx, userInfo, !!task.mad) === 5 && date < activeStageObj.date_evaluation_step5_start))
      ) {
        return true;
      } else if (
        getActiveEvaluationStep &&
        ((getActiveEvaluationStep > 2 && getActiveEvaluationStep <= 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 4) ||
          (getActiveEvaluationStep > 4 && userCodEtapa(ctx, userInfo, !!task.mad) === 5))
      ) {
        return true;
      }

      // TO DO (do original): está redundante com o IF anterior.
      // return false;
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!task.mad) &&
      userCodEtapa(ctx, null, !!task.mad) === 5 &&
      !!getActiveEvaluationStep
    ) {
      return true;
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!task.mad) &&
      userCodEtapa(ctx, null, !!task.mad) === 4
    ) {
      if (
        typeof getActiveEvaluationStep === 'number' &&
        typeof _taskStep === 'number' &&
        (getActiveEvaluationStep === 2 || (getActiveEvaluationStep > 2 && _taskStep > 2))
      ) {
        return true;
      }

      return false;
    }

    if (
      task.status === 'rejeitado' ||
      taskAppreciationExpired(ctx, task) ||
      (typeof _taskStep === 'number' && typeof getPlayerStep(ctx, task) === 'number' && _taskStep < getPlayerStep(ctx, task)) ||
      (typeof _taskStep === 'number' &&
        typeof getActiveEvaluationStep === 'number' &&
        isCurrentEvaluator(ctx, task) &&
        ((getActiveEvaluationStep === 2 && _taskStep < 2) ||
          (getActiveEvaluationStep === 3 && _taskStep < 3) ||
          (getActiveEvaluationStep === 4 && _taskStep < 4) ||
          (getActiveEvaluationStep === 5 && _taskStep < 5))) ||
      (userCodEtapa(ctx, null, !!task.mad) &&
        userCodEtapa(ctx, null, !!task.mad) === 4 &&
        String(task.original_manager) === String(playerInfo._id) &&
        !canReset(ctx, task))
    )
      return false;

    return true;
  });
  const maxDoubledTasks = currentMaxDoubledTasks.length;

  // COT-07 — cota de promoção
  const stepFourCurrentMaxDoubledTasks = currentMaxDoubledTasks.filter((task: any) => {
    const _step = getPlayerStep(ctx, task);
    return _step && _step >= 4 && getActiveEvaluationStep && getActiveEvaluationStep >= 4;
  });

  const percent = stepFourCurrentMaxDoubledTasks?.length ? 0.2 : 0.1;

  const maxPromotedTasks = currentMaxDoubledTasks.length
    ? clamp(Math.ceil(currentMaxDoubledTasks.length * percent), 1, Infinity)
    : 0;

  const validTasks = stageData.filter((item: any) => item.status !== 'rejeitado' && item.status !== 'aguardando');
  const rejectedTasks = stageData.filter((item: any) => item.status === 'rejeitado');

  // COT-08 — bonificadas atuais
  const doubledTasks = dataWithoutProsseguir.filter((task: any) => {
    const _taskStep = getTaskStepLocal(task);

    if (Array.isArray(task.prosseguir_managers) && !!task.prosseguir_managers.includes(playerInfo._id)) return false;

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!task.mad) &&
      userCodEtapa(ctx, null, !!task.mad) === 5 &&
      !!getActiveEvaluationStep
    ) {
      return task.multiplicador && task.multiplicador > 1;
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!task.mad) &&
      userCodEtapa(ctx, null, !!task.mad) === 4
    ) {
      if (
        typeof getActiveEvaluationStep === 'number' &&
        typeof task.multiplicador === 'number' &&
        ((getActiveEvaluationStep === 2 && task.stageNumber >= 2) ||
          (getActiveEvaluationStep > 2 && task.stageNumber > 2) ||
          (getActiveEvaluationStep === 4 && task.multiplicador && task.multiplicador >= 4))
      ) {
        return true;
      }

      return false;
    }

    if (typeof getTaskStepLocal(task) === 'number' && (getTaskStepLocal(task) as number) > 2) {
      if ((getTaskStepLocal(task) as number) === 5 && getPlayerStep(ctx, task) === 5 && task.status === 'selo') {
        return true;
      }

      if ((getTaskStepLocal(task) as number) > getPlayerStep(ctx, task)) {
        return true;
      }

      if (
        getActiveEvaluationStep &&
        userCodEtapa(ctx, null, !!task.mad) &&
        userCodEtapa(ctx, null, !!task.mad) === 4 &&
        String(task.original_manager) === String(playerInfo._id) &&
        (getTaskStepLocal(task) as number) > getActiveEvaluationStep
      ) {
        return true;
      }

      if (task.status === 'dobrado' && (getTaskStepLocal(task) as number) === getPlayerStep(ctx, task)) {
        return true;
      }

      if (
        (task.status === 'concluidoAuto' || task.status === 'concluido') &&
        (getTaskStepLocal(task) as number) === getPlayerStep(ctx, task) &&
        (task.multiplicador === 2 || task.multiplicador === 4)
      ) {
        return true;
      }

      return false;
    }

    return false;
  });

  // COT-08 — promovidas atuais
  const promotedTasks = dataWithoutProsseguir.filter((task: any) => {
    if (Array.isArray(task.prosseguir_managers) && !!task.prosseguir_managers.includes(playerInfo._id)) return false;

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!task.mad) &&
      userCodEtapa(ctx, null, !!task.mad) === 5 &&
      !!getActiveEvaluationStep
    ) {
      return task.status === 'selo';
    }

    if (
      String(task.original_manager) === String(playerInfo._id) &&
      userCodEtapa(ctx, null, !!task.mad) &&
      userCodEtapa(ctx, null, !!task.mad) === 4
    ) {
      if (
        typeof getActiveEvaluationStep === 'number' &&
        typeof task.multiplicador === 'number' &&
        getActiveEvaluationStep > 2 &&
        task.stageNumber > 2
      ) {
        return true;
      }

      return false;
    }

    if (typeof getTaskStepLocal(task) === 'number' && (getTaskStepLocal(task) as number) > 2) {
      if ((getTaskStepLocal(task) as number) === 5 && getPlayerStep(ctx, task) === 5 && task.status === 'selo') {
        return true;
      }

      if ((getTaskStepLocal(task) as number) > getPlayerStep(ctx, task)) {
        return true;
      }

      if (
        getActiveEvaluationStep &&
        userCodEtapa(ctx, null, !!task.mad) &&
        userCodEtapa(ctx, null, !!task.mad) === 4 &&
        String(task.original_manager) === String(playerInfo._id) &&
        (getTaskStepLocal(task) as number) > getActiveEvaluationStep
      ) {
        return true;
      }

      return false;
    }

    return false;
  });

  // COT-09 — prêmios
  const maxValeuTasks = clamp(stageData.length, 1, Infinity);
  const valeuTasks = stageData.filter((x: any) => x.status === 'valeu');

  const maxMandouBemTasks = clamp(Math.ceil(stageData.length * 0.5), 1, Infinity);
  const mandouBemTasks = stageData.filter((x: any) => x.status === 'mandou');

  const maxUauTasks = clamp(Math.ceil(stageData.length * 0.25), 1, Infinity);
  const uauTasks = stageData.filter((x: any) => x.status === 'selo');

  return {
    maxValidTasks,
    maxRejectedTasks,
    feedbackTasks,
    dataWithoutProsseguir,
    maxDoubledTasks,
    maxPromotedTasks,
    validTasks,
    rejectedTasks,
    doubledTasks,
    promotedTasks,
    maxValeuTasks,
    valeuTasks,
    maxMandouBemTasks,
    mandouBemTasks,
    maxUauTasks,
    uauTasks,
  };
}

// CAS-01 — cascata direta: promoção que fecha a cota converte as demais `dobrado`
// do mesmo multiplicador em concluidoAuto — port de index.tsx:2357-2360 + 2374-2394.
// A cota é recalculada como no original: promovidas atuais sem a task nova + ela.
export function selectCascadeTargets(
  ctx: NormalRuleContext,
  filteredData: any[],
  newObj: any,
  canResetFn: (task: any) => boolean,
): any[] {
  const playerInfo: any = ctx.playerInfo;
  const { getActiveEvaluationStep, promotedTasks, maxPromotedTasks } = ctx;

  const _promotedTasks = promotedTasks.filter((x: any) => x._id !== newObj._id);
  _promotedTasks.push(newObj);

  const cotaReached = _promotedTasks.length >= maxPromotedTasks;

  return [...(filteredData as any[])].filter((x: any) => {
    if (
      x._id !== newObj._id &&
      canResetFn(x) &&
      x.status === 'dobrado' &&
      x.multiplicador === newObj.multiplicador &&
      cotaReached &&
      !(
        getActiveEvaluationStep &&
        getActiveEvaluationStep < 4 &&
        (String(x.original_manager) === String(playerInfo._id) ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))) &&
        (userCodEtapa(ctx, null, !!x.mad) === 4 || userCodEtapa(ctx, null, !!x.mad) === 5)
      )
    ) {
      return true;
    }

    return false;
  });
}

// CAS-04 — cascata reversa: reset de promovido/selo devolve os concluidoAuto do
// mesmo step (da task nova OU da antiga) para dobrado — port de index.tsx:2420-2432.
export function selectCascadeReversal(
  ctx: NormalRuleContext,
  filteredData: any[],
  newObj: any,
  oldObj: any,
  canResetFn: (task: any) => boolean,
): any[] {
  return [...(filteredData as any[])].filter((x: any) => {
    if (
      x._id !== newObj._id &&
      canResetFn(x) &&
      x.status === 'concluidoAuto' &&
      (getTaskStepLocal(x) === getTaskStepLocal(newObj) || getTaskStepLocal(x) === getTaskStepLocal(oldObj)) &&
      (oldObj?.status === 'promovido' || oldObj?.status === 'selo')
    ) {
      return true;
    }

    return false;
  });
}
